import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Center,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  Spinner,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronRight } from "react-icons/fi";
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  type MinimumRequirementDto,
  type SelectionCriterionDto,
  type TemplateContentDto,
  useCreateSourcingEventTemplateMutation,
  useGetSourcingEventQuery,
  useListSourcingEventTemplatesQuery,
  useUpdateSourcingEventTemplateMutation,
} from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../common/errors/useApiError";
import { buildTemplateContent, parseSections } from "../../common/library/templateFromEvent";
import { useRunsPublicProcurements } from "../../common/organization/useRunsPublicProcurements";
import { urls } from "../../urls";
import { TemplateAwardSection } from "./TemplateAwardSection";
import { TemplateMinimumSection } from "./TemplateMinimumSection";
import { TemplateQualificationSection } from "./TemplateQualificationSection";
import { useRecordTitle } from "../../common/page-title/PageTitle";

// t('New template') t('Edit template') t('Templates') t('Cancel') t('Save template')
// t('Template name') t('Category (optional)') t('E.g. IT procurement') t('E.g. IT and digitalization')
// t('Description (optional)') t('E.g. Standard template for buying software and IT services (SaaS).')
// t('Template not found')

export const MalEditor = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = id != null;
  const authState = useLoggedInWithOrgContextState();
  const runsPublicProcurements = useRunsPublicProcurements();
  const { trigger } = useApiError();

  const { data, isLoading: isListLoading, error } = useListSourcingEventTemplatesQuery({});
  const existing = isEdit ? data?.templates.find((tmpl) => tmpl.id === id) : undefined;
  useRecordTitle(existing?.name);

  // A new template can be seeded from a live event's requirements ("save as template" in the wizard).
  // The source and its scope travel in the URL rather than in navigation state, so a reload rebuilds
  // the same draft instead of silently emptying it. Nothing is persisted until Save.
  const [searchParams] = useSearchParams();
  const fromEvent = !isEdit ? searchParams.get("fromEvent") : null;
  const prefillSections = useMemo(() => parseSections(searchParams.get("sections")), [searchParams]);
  const {
    data: sourceEvent,
    isLoading: isEventLoading,
    error: eventError,
  } = useGetSourcingEventQuery(fromEvent ? { eventId: fromEvent } : skipToken);
  // Global (SourceMagnet standard) templates may only be managed by a system admin; org templates by an org admin.
  const canManage = existing?.owner.global ? authState.me.isSystemAdmin : authState.selectedOrg.canEditDocumentTemplates;

  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [content, setContent] = useState<TemplateContentDto>({});
  const showsQualificationSection = runsPublicProcurements;
  const initializedId = useRef<string | undefined>(undefined);

  const [createTemplate, { isLoading: isCreating }] = useCreateSourcingEventTemplateMutation();
  const [updateTemplate, { isLoading: isUpdating }] = useUpdateSourcingEventTemplateMutation();
  const isSaving = isCreating || isUpdating;

  // Re-initialize when the :id param changes — the route does not remount when navigating between two
  // edit URLs, so a plain one-shot guard would keep the previous template's data.
  useEffect(() => {
    if (isEdit && existing && initializedId.current !== id) {
      setName(existing.name);
      setCategory(existing.category ?? "");
      setDescription(existing.description ?? "");
      setContent(existing.content);
      initializedId.current = id;
    }
  }, [isEdit, existing, id]);

  // Seed the draft once. The name is deliberately left blank: Save stays disabled until the user gives
  // the template a deliberate name rather than inheriting one procurement's title.
  const prefilled = useRef(false);
  useEffect(() => {
    if (sourceEvent && !prefilled.current) {
      setContent(buildTemplateContent(sourceEvent.eventData, prefillSections));
      prefilled.current = true;
    }
  }, [sourceEvent, prefillSections]);

  useEffect(() => {
    if (error) trigger(error);
  }, [error, trigger]);

  // A source event that fails to load leaves an empty draft rather than a blocking error page — the
  // user can still build the template by hand.
  useEffect(() => {
    if (eventError) trigger(eventError);
  }, [eventError, trigger]);

  const setMinimum = (items: MinimumRequirementDto[]) => setContent((c) => ({ ...c, minimumRequirements: items }));
  const setSelection = (items: SelectionCriterionDto[]) => setContent((c) => ({ ...c, selectionCriteria: items }));

  const save = async () => {
    // An untouched award section is saved as absent, not as an implicit "lowest price": the template
    // then leaves a procurement's award model alone when applied, the same way the requirement lists
    // do when they are empty.
    try {
      if (isEdit && id) {
        await updateTemplate({
          updateSourcingEventTemplateRequest: {
            id,
            name: name.trim(),
            category: category || null,
            description: description.trim() || null,
            content,
          },
        }).unwrap();
      } else {
        await createTemplate({
          createSourcingEventTemplateRequest: {
            name: name.trim(),
            category: category || null,
            description: description.trim() || null,
            content,
            global: false,
          },
        }).unwrap();
      }
      navigate(urls.app.maler.list);
    } catch (e) {
      trigger(e);
    }
  };

  // Wait for the source event before the first paint, otherwise the sections flash empty and then fill.
  if (fromEvent && isEventLoading) {
    return (
      <Center py="20">
        <Spinner color="smPrimary" />
      </Center>
    );
  }
  if (isEdit && isListLoading && !data) {
    return (
      <Center py="20">
        <Spinner color="smPrimary" />
      </Center>
    );
  }
  if (!isEdit && authState.selectedOrg.subscription?.hasBuyerAccess === false) {
    return (
      <Center py="20">
        <Text color="smMuted">{t("zz_SubscriptionApiErrorCode_EventOperationNotIncluded")}</Text>
      </Center>
    );
  }
    if (isEdit && data && !existing) {
    return (
      <Center py="20">
        <Text color="smMuted">{t("Template not found")}</Text>
      </Center>
    );
  }

  return (
    <VStack align="stretch" spacing="6">
      <Box>
        <Breadcrumb
          spacing="2"
          separator={<Icon as={FiChevronRight} color="smMuted" />}
          fontSize="sm"
          color="smMuted"
          mb="2"
        >
          <BreadcrumbItem>
            <BreadcrumbLink as={RouterLink} to={urls.app.maler.list}>
              {t("Templates")}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink>{isEdit ? name || t("Edit template") : t("New template")}</BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>
        <Flex justify="space-between" align="center" gap="3">
          <Heading size="lg" color="smText">
            {t(isEdit ? "Edit template" : "New template")}
          </Heading>
          <Flex gap="3" flexShrink={0}>
            <Button size="sm" variant="outline" onClick={() => navigate(urls.app.maler.list)} isDisabled={isSaving}>
              {t("Cancel")}
            </Button>
            <Button size="sm" colorScheme="teal" onClick={save} isLoading={isSaving} isDisabled={!canManage || name.trim() === ""}>
              {t("Save template")}
            </Button>
          </Flex>
        </Flex>
      </Box>

      <Flex gap="4" flexWrap="wrap">
        <FormControl flex="1" minW="280px" isRequired>
          <FormLabel>{t("Template name")}</FormLabel>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("E.g. IT procurement") ?? ""} />
        </FormControl>
        <FormControl flex="1" minW="280px">
          <FormLabel>{t("Category (optional)")}</FormLabel>
          <Input
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder={t("E.g. IT and digitalization") ?? ""}
          />
        </FormControl>
      </Flex>

      <FormControl>
        <FormLabel>{t("Description (optional)")}</FormLabel>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder={t("E.g. Standard template for buying software and IT services (SaaS).") ?? ""}
        />
      </FormControl>

      <TemplateMinimumSection items={content.minimumRequirements ?? []} onChange={setMinimum} />
      {showsQualificationSection && (
        <TemplateQualificationSection items={content.selectionCriteria ?? []} onChange={setSelection} />
      )}
      <TemplateAwardSection
        awardCriteria={content.awardCriteria}
        priceModel={content.priceModel}
        onChangeAward={(v) => setContent((c) => ({ ...c, awardCriteria: v ?? null }))}
        onChangePriceModel={(v) => setContent((c) => ({ ...c, priceModel: v ?? null }))}
      />
    </VStack>
  );
};
